import { Ruler, Truck, Wrench, Bell, PackageOpen } from "lucide-react";

/**
 * Job Type badge — surfaces the "what kind of visit is this?" flag on
 * calendar rows, day cards, and the JobDialog banner.
 *
 * Types:
 *   on_site         → default screen work; hidden by default (no visual noise)
 *   measure         → tech is measuring only (usually no cut list)
 *   deliver_install → dropping off / installing prebuilt screens
 *   pickup          → customer dropped off; screens left at tech's address for pickup
 *   reminder        → internal calendar note (no customer, no SMS, no slot block)
 *
 * Sizes: xs (week-view pill) / sm (day-view pill) / md (dialog banner)
 */
export function JobTypeBadge({ jobType, size = "sm", showOnSite = false }) {
  const type = (jobType || "on_site").toLowerCase();

  if (type === "on_site" && !showOnSite) return null;

  const configs = {
    on_site: {
      label: "On-Site",
      short: "On-Site",
      icon: Wrench,
      colors: "border-zinc-300 bg-zinc-50 text-zinc-700",
    },
    measure: {
      label: "Measure",
      short: "Measure",
      icon: Ruler,
      colors: "border-blue-400 bg-blue-50 text-blue-700",
    },
    deliver_install: {
      label: "Deliver / Install",
      short: "Deliver",
      icon: Truck,
      colors: "border-purple-400 bg-purple-50 text-purple-700",
    },
    pickup: {
      label: "Pick Up",
      short: "Pick Up",
      icon: PackageOpen,
      colors: "border-emerald-500 bg-emerald-50 text-emerald-800",
    },
    reminder: {
      label: "Reminder",
      short: "Reminder",
      icon: Bell,
      colors: "border-amber-400 bg-amber-50 text-amber-800",
    },
  };

  const cfg = configs[type] || configs.on_site;
  const Icon = cfg.icon;

  if (size === "xs") {
    return (
      <span
        data-testid={`job-type-badge-xs-${type}`}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.colors}`}
        title={cfg.label}
      >
        <Icon className="w-2.5 h-2.5" />
        {cfg.short}
      </span>
    );
  }

  if (size === "md") {
    return (
      <span
        data-testid={`job-type-badge-md-${type}`}
        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold border-2 ${cfg.colors}`}
      >
        <Icon className="w-4 h-4" />
        {cfg.label}
      </span>
    );
  }

  return (
    <span
      data-testid={`job-type-badge-sm-${type}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.colors}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}
